/**
 * Wearitto header scroll behaviour.
 *
 * Targets `sticky-header.header-wrapper` directly (NOT the whole header
 * group). The announcement bar is a sibling section that stays in normal
 * flow — it scrolls away naturally. Only the header itself stays fixed.
 *
 * Classes toggled on sticky-header:
 *  - `is-scrolled` — past a small scroll threshold. CSS swaps the
 *    floating-blur appearance for the solid + hairline state.
 *  - `is-hidden`   — while scrolling DOWN past that threshold. CSS
 *    translates the header off-screen. Removed on scroll-up.
 *
 * Also publishes --header-offset (header's rendered height) for any
 * layout that needs to reserve space.
 */
(function () {
  function init() {
    var header = document.querySelector('sticky-header.header-wrapper');
    if (!header) return;
    setupOffset(header);
    new HeaderScroll(header);
  }

  function setupOffset(header) {
    var update = function () {
      document.documentElement.style.setProperty(
        '--header-offset',
        header.offsetHeight + 'px'
      );
    };
    update();
    window.addEventListener('resize', update, { passive: true });
    window.addEventListener('load', update, { once: true });
  }

  function HeaderScroll(header) {
    this.header = header;
    this.thresholdShow = 80;
    this.directionDelta = 6;
    this.lastY = window.scrollY;

    // Cache the announcement bar and its height ONCE — re-reading
    // offsetHeight on every scroll frame is a layout-thrashing
    // forced reflow that kills scroll performance.
    this.bar = document.querySelector('.announcement-bar');
    this.barH = this.bar ? this.bar.offsetHeight : 0;
    this.lastTopValue = -1; // sentinel so first set writes through
    this.lastIsHidden = false;
    this.lastIsScrolled = false;

    this.attach();
    this.update();
  }

  HeaderScroll.prototype.attach = function () {
    var self = this;
    var ticking = false;
    var handler = function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        self.update();
      });
    };
    window.addEventListener('scroll', handler, { passive: true });

    // Resize is the only thing that should re-measure the bar height.
    window.addEventListener(
      'resize',
      function () {
        if (self.bar) self.barH = self.bar.offsetHeight;
        handler();
      },
      { passive: true }
    );
  };

  HeaderScroll.prototype.update = function () {
    var y = window.scrollY;
    var diff = y - this.lastY;

    // --header-top tracks the announcement bar's remaining visible
    // height. Only write to the style when the value actually changes,
    // and only compute while we're still in the bar's range.
    if (this.bar) {
      var top = y < this.barH ? this.barH - y : 0;
      if (top !== this.lastTopValue) {
        document.documentElement.style.setProperty('--header-top', top + 'px');
        this.lastTopValue = top;
      }
    }

    var wantIsScrolled, wantIsHidden;
    if (y < this.thresholdShow) {
      wantIsScrolled = false;
      wantIsHidden = false;
    } else {
      wantIsScrolled = true;
      if (Math.abs(diff) > this.directionDelta) {
        wantIsHidden = diff > 0;
      } else {
        wantIsHidden = this.lastIsHidden; // no jitter
      }
    }

    if (wantIsScrolled !== this.lastIsScrolled) {
      this.header.classList.toggle('is-scrolled', wantIsScrolled);
      this.lastIsScrolled = wantIsScrolled;
    }
    if (wantIsHidden !== this.lastIsHidden) {
      this.header.classList.toggle('is-hidden', wantIsHidden);
      this.lastIsHidden = wantIsHidden;
    }
    this.lastY = y;
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
